import { Injectable, inject } from '@angular/core';
import { Observable, finalize, of, shareReplay, tap } from 'rxjs';
import { EventService } from '../event.service';
import { XploreItemDTO } from '../../shared/object/xplorer-be-parsed-classes';

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class EventCacheService {
  private static readonly TTL_MS = 900_000; // 15 min

  private readonly eventService = inject(EventService);

  private cache: CacheEntry<XploreItemDTO[]> | null = null;
  private inFlight$: Observable<XploreItemDTO[]> | null = null;

  getUpcomingPopularEvents(forceRefresh = false): Observable<XploreItemDTO[]> {
    if (!forceRefresh) {
      const cached = this.getValidEntry(this.cache);
      if (cached) return of(cached.value);
      if (this.inFlight$) return this.inFlight$;
    }

    let request$: Observable<XploreItemDTO[]>;
    request$ = this.eventService.getUpcomingPopularEvents().pipe(
      tap(events => { this.cache = this.createEntry(events); }),
      finalize(() => { if (this.inFlight$ === request$) this.inFlight$ = null; }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.inFlight$ = request$;
    return request$;
  }

  invalidate(): void {
    this.cache = null;
    this.inFlight$ = null;
  }

  private createEntry<T>(value: T): CacheEntry<T> {
    return { value, expiresAt: Date.now() + EventCacheService.TTL_MS };
  }

  private getValidEntry<T>(entry: CacheEntry<T> | null): CacheEntry<T> | null {
    if (!entry || entry.expiresAt <= Date.now()) return null;
    return entry;
  }
}
